/**
 * Gallery Animation Manager
 * Handles artist image gallery with infinite scroll animation
 */

/**
 * Artist images for gallery (base names without extension)
 */
const ARTIST_IMAGES = [
  'adele',
  'taylor_swift',
  'ed_sheeran',
  'beyonce',
  'bruno_mars',
  'ariana_grande',
  'john_legend',
  'whitney',
  'beatles',
  'freddie_mercury',
  'elvis_presley',
  'lady_gaga',
  'celine_dion',
  'michael_jackson',
  'rihanna',
  'justin_bieber',
  'dua_lipa',
  'harry_styles',
  'olivia_rodrigo',
  'miley_cyrus',
  'abba',
  'amy',
  'aretha',
  'backstreet_boys',
  'bts',
  'bob_marley',
  'chris_brown',
  'cold_play',
  'cooper_and_gaga',
  'cyndi',
  'eurythmics',
  'giveon',
  'hozier',
  'imagine_dragons',
  'journey',
  'laufey',
  'louis_armstrong',
  'maneskin',
  'nat_king_cole',
  'nirvana',
  'one_direction',
  'paramore',
  'radiohead',
  'sabrina_carpenter',
  'shakira',
  'usher',
  'bill_withers',
  'carrie_underwood',
  'gaynor',
  'jvke',
  'kirk_franklin',
  'lauren_daigle',
  'one_republic',
  'platters',
  'thalia',
  'tori_kelly',
  'alicia_keys',
  'bebe_rexha',
  'bastille',
  'benson_boone',
];

// Check if browser supports AVIF
let supportsAvif: boolean | null = null;
function checkAvifSupport(): Promise<boolean> {
  if (supportsAvif !== null) return Promise.resolve(supportsAvif);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { supportsAvif = true; resolve(true); };
    img.onerror = () => { supportsAvif = false; resolve(false); };
    // Tiny AVIF image
    img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABhAQ0AIy';
  });
}

// Get image URL with AVIF support
function getArtistImageUrl(imgBase: string): string {
  if (supportsAvif) {
    return `assets/images/artists/${imgBase}.avif`;
  }
  return `assets/images/artists/${imgBase}.jpg`;
}

// Initialize AVIF support check
checkAvifSupport();

/**
 * Gallery Manager
 */
export class GalleryManager {
  private artistImages = ARTIST_IMAGES;

  /**
   * Initialize gallery
   */
  init(): void {
    this.setupInfiniteScroll();
    this.populateInitialGrid();
  }

  /**
   * Setup infinite scroll animation
   */
  private setupInfiniteScroll(): void {
    const gallery = document.querySelector('.artists-gallery-bg') as HTMLElement;
    if (!gallery) return;

    // Clone the grid content for seamless loop
    const tiles = gallery.querySelectorAll('.artist-tile');

    // Duplicate tiles to fill double height
    tiles.forEach((tile) => {
      const clone = tile.cloneNode(true) as HTMLElement;
      gallery.appendChild(clone);
    });

    // Add more tiles if needed to ensure full coverage
    const viewportHeight = window.innerHeight;
    const tileHeight = 100;
    const rowsNeeded = Math.ceil((viewportHeight * 2) / tileHeight);
    const tilesPerRow = Math.floor((window.innerWidth * 1.2) / 100);
    const totalNeeded = rowsNeeded * tilesPerRow;

    while (gallery.children.length < totalNeeded) {
      this.addRandomTile(gallery);
    }
  }

  /**
   * Populate initial grid
   */
  private populateInitialGrid(): void {
    const gallery = document.querySelector('.artists-gallery-bg') as HTMLElement;
    if (!gallery || gallery.children.length > 0) return;

    // Generate initial grid
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const tileSize = 100;
    const rows = Math.ceil((viewportHeight * 2) / tileSize);
    const cols = Math.ceil((viewportWidth * 1.2) / tileSize);

    for (let i = 0; i < rows * cols; i++) {
      this.addRandomTile(gallery);
    }
  }

  /**
   * Add random tile to gallery
   */
  private addRandomTile(gallery: HTMLElement): void {
    const tile = document.createElement('div');
    tile.className = 'artist-tile';

    // Randomly make some tiles large (20% chance)
    if (Math.random() < 0.2) {
      tile.className += ' tile-large';
    }

    const randomIndex = Math.floor(Math.random() * this.artistImages.length);
    const randomImage = this.artistImages[randomIndex]!;
    const img = document.createElement('img');
    img.src = getArtistImageUrl(randomImage);
    img.alt = '';

    tile.appendChild(img);
    gallery.appendChild(tile);
  }

  /**
   * Optimize animation performance
   */
  optimizeAnimation(): void {
    const gallery = document.querySelector('.artists-gallery-bg') as HTMLElement;
    if (!gallery) return;

    // Use will-change for better performance
    gallery.style.willChange = 'transform';

    // Clean up will-change after animation starts
    setTimeout(() => {
      gallery.style.willChange = 'auto';
    }, 1000);
  }
}

// Create singleton instance
export const galleryManager = new GalleryManager();

// Re-initialize when navigating to songs screen
if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', () => {
    if (window.location.hash === '#songs') {
      galleryManager.init();
    }
  });
}
